import { createContext } from 'react'
import type { CartLine } from '../../shared/types'
import { type CartPageCartItemMessageInfoFragment } from '@stuller/stullercom/data-access/apollo-queries'
import { IMAGE_COMING_SOON } from '@stuller/shared/util/constants'

export interface CartItemContextType {
  /**
   * The whole cart line object
   */
  cartLine: CartLine
  /**
   * Disable input fields in the cart item
   */
  disableFormChanges: boolean
  /**
   * Indicates the cart item is loading
   */
  loading: boolean
  /**
   * Update the loading state of the cart item
   */
  setLoading: (loading: boolean) => void
  /**
   * Indicates the cart item made a change successfully
   */
  success: boolean
  /**
   * Update the success state of the cart item
   */
  setSuccess: (success: boolean) => void
  /**
   * Indicates the cart item errored
   */
  error: CartPageCartItemMessageInfoFragment[]
  /**
   * Update the error state of the cart item
   */
  setError: (error: CartPageCartItemMessageInfoFragment[]) => void
  /**
   * Indicates the cart item is a configuration
   */
  isConfiguration: boolean
  /**
   * Indicates the cart item is a serialized product
   */
  isSerializedProduct: boolean
  /**
   * The image URL of the cart item
   */
  imageUrl: string
  /**
   * The title of the item
   */
  itemTitle: string
  /**
   * The URL of the cart item
   */
  itemUrl: string
  /**
   * The item number of the cart item
   */
  itemNumber?: string
  /**
   * Indicates the cart item can be matched
   */
  canMatch: boolean
  /**
   * Indicates the cart item is matched
   */
  isMatched: boolean
  /**
   * Indicates the cart item can be annealed
   */
  canAnneal: boolean
  /**
   * Indicates the cart item is annealed
   */
  isAnnealed: boolean
  /**
   * Indicates the cart item can be ordered on consignment
   */
  canOrderOnConsignment: boolean
  /**
   * Indicates the cart item is for consignment
   */
  isForConsignment: boolean
  /**
   * Indicates the cart item is non-returnable
   */
  isNonReturnable: boolean
  /**
   * Indicates the cart item is limited availability
   */
  isLimitedAvailability: boolean
  /**
   * Indicates the cart item configuration can be edited
   */
  canEditItem: boolean
}

const CartItemContext = createContext<CartItemContextType>({
  cartLine: {} as unknown as CartLine,
  disableFormChanges: false,
  loading: false,
  setLoading: () => {},
  success: false,
  setSuccess: () => {},
  error: [],
  setError: () => {},
  isConfiguration: false,
  isSerializedProduct: false,
  imageUrl: IMAGE_COMING_SOON,
  itemTitle: '',
  itemUrl: '',
  canMatch: false,
  isMatched: false,
  canAnneal: false,
  isAnnealed: false,
  canOrderOnConsignment: false,
  isForConsignment: false,
  isNonReturnable: false,
  isLimitedAvailability: false,
  canEditItem: false
})

export {
  CartItemContext
}
